import React, { useState, useEffect, useRef } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Drawer, Toolbar, Box } from '@mui/material';
import Topbar from './Topbar';
import { Context } from './Context';
import Sidebar from './components/Sidebar';
import useMediaQuery from '@mui/material/useMediaQuery';
import { collection, onSnapshot, query, orderBy, limit, doc, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import BuildInfo from './BuildInfo';



const theme = createTheme();
const drawerWidth = 240;

type Selected = { lineId: string; routeId: string };

const Layout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // default selection (safe fallback)
  const [selected, setSelected] = useState<Selected | null>(null);

  const openSidebar = useMediaQuery(theme.breakpoints.up('lg'));
  const [forceOpen, setForceOpen] = useState(false);


  const didInitRef = useRef(false);

  useEffect(() => {
    if (didInitRef.current) return;

    const qLines = query(collection(db, "lines"), orderBy("updatedAt", "desc"), limit(1));

    const unsub = onSnapshot(qLines, async (snap) => {
      if (didInitRef.current) return;
      if (snap.empty) return;

      const lineId = snap.docs[0].id;

      const qRoutes = query(
        collection(db, "lines", lineId, "routes"),
        orderBy("updatedAt", "desc"),
        limit(1)
      );

      const routesSnap = await getDocs(qRoutes);
      if (didInitRef.current) return;
      if (routesSnap.empty) return;

      const routeId = routesSnap.docs[0].id;

      setSelected({ lineId, routeId });
      didInitRef.current = true;
      unsub();
    });

    return () => unsub();
  }, []);

  return (
    <ThemeProvider theme={theme}>    
      <Context.Provider value={{ selected, setSelected }}>
        <CssBaseline />
        <Topbar menuState={forceOpen} openAction={setForceOpen} />

        <Box sx={{ display: 'flex' }}>
          <Drawer
            variant="temporary"
            open={openSidebar || forceOpen}
            hideBackdrop={true}
            sx={{
              width: drawerWidth,
              flexShrink: 0,
              [`& .MuiDrawer-paper`]: { width: drawerWidth, boxSizing: 'border-box', bgcolor: '#18364a' },
            }}
            onClick={() => setForceOpen(false)}
          >
            <Sidebar />
          </Drawer>

          <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3, mt: 8 }}>
            <Toolbar />
            {children}
          </Box>
        </Box>
        <BuildInfo />
      </Context.Provider>
    </ThemeProvider>
  );
};

export default Layout;